import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Calculator, PieChart, ClipboardCheck, ArrowRight, Check, X, Info, HelpCircle, AlertTriangle, Zap, Shield, Star, BadgeDollarSign, Brain, Download, Loader2, ChevronRight, Ban, Share2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { salaryDataByLocation, ROLES, stageMultipliers, currencyOptions } from "../data/salaryData";

// Dilution estimates per stage
const dilutionRates = {
  "Pre-Seed / Seed": { toExit: 0.65, rounds: "3-4 more rounds typical" },
  "Series A": { toExit: 0.50, rounds: "2-3 more rounds typical" },
  "Series B": { toExit: 0.35, rounds: "1-2 more rounds typical" },
  "Series C+": { toExit: 0.20, rounds: "0-1 more rounds typical" },
};

// Reusable "Share result" button — Web Share on mobile, clipboard fallback.
// Preserves the ?ref= referral tag so shared links keep attribution.
const ShareResult = ({ hash, summary }) => {
  const { search } = useLocation();
  const ref = new URLSearchParams(search).get("ref");
  const handleShare = async () => {
    const url = `${window.location.origin}/tools${ref ? `?ref=${encodeURIComponent(ref)}` : ""}#${hash}`;
    const text = summary ? `${summary}\n\nTry Magnus Kor's free startup tools:` : "Try Magnus Kor's free startup tools:";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Magnus Kor Startup Tools", text, url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Result link copied to clipboard");
      } else {
        toast.info("Copy this link to share: " + url);
      }
    } catch (e) {
      /* user dismissed the share sheet — no-op */
    }
  };
  return (
    <button
      onClick={handleShare}
      data-testid={`share-${hash}`}
      className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-[#B8860B] transition-colors"
    >
      <Share2 size={14} /> Share result
    </button>
  );
};

const ToolsPage = () => {
  const tabToHash = { salary: "salary-calculator", equity: "equity-calculator", checklist: "hiring-readiness", cost: "hiring-cost-calculator", planner: "smart-hire-planner" };
  const hashToTab = Object.fromEntries(Object.entries(tabToHash).map(([k, v]) => [v, k]));
  const location = useLocation();

  const getInitialTab = () => {
    const h = location.hash.replace("#", "");
    return hashToTab[h] || "salary";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const h = location.hash.replace("#", "");
    if (hashToTab[h] && hashToTab[h] !== activeTab) setActiveTab(hashToTab[h]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const switchTab = (id) => {
    setActiveTab(id);
    window.history.replaceState(null, "", `#${tabToHash[id]}`);
  };

  const toolsFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Are Magnus Kor's hiring tools free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. All tools including the Global Salary Calculator, Hiring Cost Calculator, Equity Calculator, Hiring Readiness Checklist, and AI Smart Hire Planner are completely free to use." }},
      { "@type": "Question", "name": "How accurate is the Salary Calculator?", "acceptedAnswer": { "@type": "Answer", "text": "Our salary data covers 49 countries and 8 core tech roles. Benchmarks are based on aggregated market data from VC-backed SaaS startups and are updated regularly to reflect current market conditions." }},
      { "@type": "Question", "name": "What does the AI Smart Hire Planner do?", "acceptedAnswer": { "@type": "Answer", "text": "The Smart Hire Planner uses AI (GPT-4o) to generate a personalized hiring roadmap based on your startup's stage, team size, goals, and budget. It recommends which roles to hire, in what order, and at what compensation." }},
      { "@type": "Question", "name": "Can I calculate hiring costs in different currencies?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. The Hiring Cost Calculator supports 38 currencies and factors in recruiting fees, onboarding costs, equity value, and time-to-productivity to give you the true total cost of a hire." }},
      { "@type": "Question", "name": "How does the Equity Calculator work?", "acceptedAnswer": { "@type": "Answer", "text": "Enter the equity percentage, company valuation, vesting schedule, and funding stage. The calculator models dilution across future rounds and shows projected value at different exit scenarios." }}
    ]
  };

  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO
        title="Free Startup Tools - Salary Calculator, Equity Calculator, AI Hire Planner"
        description="Free tools for startup founders: Global Salary Calculator (49 countries), Hiring Cost Calculator (38 currencies), Equity Calculator, Hiring Readiness Checklist, and AI Smart Hire Planner powered by GPT-4o."
        keywords="startup salary calculator, SaaS compensation benchmarks, equity calculator startups, hiring cost calculator, startup hiring readiness, AI hiring planner, global salary data, Series A compensation, tech salary benchmarks 2026, startup equity vesting, free hiring tools"
        canonical="https://magnuskor.com/tools"
        jsonLd={toolsFaqSchema}
      />

      {/* Header */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <span className="text-overline">Free Resources</span>
          <h1 className="heading-hero mt-4 mb-6">Startup Tools</h1>
          <p className="text-body-lg max-w-2xl mb-6">
            Make better hiring and career decisions with our free tools. Built from real market data and our experience working with VC-backed startups.
          </p>
          <button
            onClick={() => setShowGuide(!showGuide)}
            className="inline-flex items-center gap-2 text-sm text-[#B8860B] hover:underline"
          >
            <HelpCircle size={16} />
            {showGuide ? "Hide" : "Show"} How to Use These Tools
          </button>
        </div>
      </section>

      {/* Guide Section */}
      {showGuide && (
        <section className="py-8 bg-[#1A1A1A] text-white">
          <div className="max-w-7xl mx-auto px-8 md:px-16">
            <h2 className="font-display text-xl mb-6">How to Use These Tools</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-[#B8860B] font-medium mb-2">Salary Calculator</h3>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li>• Select role type that best matches the position</li>
                  <li>• Choose seniority level (IC = Individual Contributor)</li>
                  <li>• Pick the primary work location (affects currency)</li>
                  <li>• Select company's current funding stage</li>
                  <li>• Results show base salary + typical equity ranges</li>
                </ul>
              </div>
              <div>
                <h3 className="text-[#B8860B] font-medium mb-2">Equity Calculator</h3>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li>• Enter your equity percentage (e.g., 0.5 for 0.5%)</li>
                  <li>• Enter current company valuation in millions</li>
                  <li>• Select current funding stage</li>
                  <li>• Results show potential value at various exit sizes</li>
                  <li>• Dilution estimates based on typical funding patterns</li>
                </ul>
              </div>
              <div>
                <h3 className="text-[#B8860B] font-medium mb-2">Hiring Readiness</h3>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li>• Select the role type you're hiring for</li>
                  <li>• Answer each question honestly (Yes/No)</li>
                  <li>• Higher scores indicate better preparation</li>
                  <li>• Review "areas to address" before starting search</li>
                  <li>• 80%+ score suggests you're ready to hire</li>
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-[#B8860B] font-medium mb-2">Hiring Cost Calculator</h3>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li>• Enter the CTC (Cost to Company) for the role you're hiring</li>
                  <li>• Select currency and number of planned hires</li>
                  <li>• Choose Growth Partner retainer duration (months)</li>
                  <li>• Compare all 3 engagement models side by side</li>
                  <li>• "Best Value" badge highlights the cheapest option</li>
                </ul>
              </div>
              <div>
                <h3 className="text-[#B8860B] font-medium mb-2">Smart Hire Planner (AI-Powered)</h3>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li>• <strong>Step 1 — Company Profile:</strong> Describe your business, funding stage, revenue, and team size. The AI uses this to calibrate recommendations to your exact growth phase.</li>
                  <li>• <strong>Step 2 — Goals & Context:</strong> Set your primary goal, bottleneck, hiring budget, preferred work mode, and location. Optionally specify a <strong>Target Role</strong> (e.g., "Senior Product Manager") for role-specific outputs.</li>
                  <li>• <strong>Priorities & Culture:</strong> Select Company Priorities (multi-select) and Culture Traits you value. This shapes the candidate profile and interview questions.</li>
                  <li>• <strong>Team Gaps:</strong> Describe your current team gaps so the AI knows where you're already covered.</li>
                </ul>
                <p className="text-xs text-[#B8860B] font-medium mt-4 mb-2">What You'll Receive:</p>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li>• <strong>Stage-Fit Score (0-100):</strong> Color-coded assessment of how well the plan fits your stage. Green (70+) = good fit, Yellow (50-69) = moderate, Red (&lt;50) = reconsider.</li>
                  <li>• <strong>Hiring Plan:</strong> Prioritised roles with salary ranges, equity, skills, experience requirements, 90-day expectations, ROI estimates, and hiring risk.</li>
                  <li>• <strong>Job Description:</strong> Ready-to-post JD for your primary hire — about section, responsibilities, requirements, and compensation summary.</li>
                  <li>• <strong>Interview Framework:</strong> 4-stage interview process (Screen → Technical → Culture → Founder) with specific, behavioural questions tailored to your stage.</li>
                  <li>• <strong>Candidate Sources:</strong> Primary and secondary sourcing channels specific to the role and location, plus pro tips.</li>
                  <li>• <strong>Hiring Strategy:</strong> Approach, timeline, key selling points to attract candidates, and red flags to watch.</li>
                  <li>• <strong>ROI Analysis:</strong> Total investment, expected return, savings vs over-hiring, and break-even estimate.</li>
                  <li>• <strong>PDF Report:</strong> Download the full branded report for internal use or sharing with investors and co-founders.</li>
                </ul>
                <p className="text-xs text-gray-400 mt-3 italic">Tip: For best results, be specific about your business description and target role. The more context you provide, the more tailored the recommendations.</p>
              </div>
            </div>

            <div className="p-4 border border-yellow-600/30 bg-yellow-900/20 rounded">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-500 mb-1">Important Disclaimers</p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• <strong>Estimates only:</strong> All figures are approximations based on aggregated market data. Actual compensation varies significantly.</li>
                    <li>• <strong>Not financial advice:</strong> The equity calculator provides rough estimates. Consult a financial advisor for personal decisions.</li>
                    <li>• <strong>Data sources:</strong> Figures derived from Levels.fyi, Glassdoor, Carta, AngelList, and our direct placement experience.</li>
                    <li>• <strong>Market conditions:</strong> Startup compensation fluctuates with funding environment, company performance, and candidate negotiation.</li>
                    <li>• <strong>Equity risks:</strong> Startup equity may be worth $0. Most startups fail. Only ~10% of funded startups have meaningful exits.</li>
                    <li>• <strong>Hiring Cost Calculator:</strong> Fee structures are indicative and subject to a signed engagement agreement. Actual fees may vary by role complexity and scope.</li>
                    <li>• <strong>Smart Hire Planner:</strong> AI-generated recommendations are strategic guidance based on typical patterns for your stage, not a guarantee. Salary figures are grounded in market data but may differ based on your specific circumstances.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Tool Tabs */}
      <section className="py-8 bg-white border-b border-gray-100 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "salary", label: "Salary Calculator", icon: Calculator },
              { id: "equity", label: "Equity Calculator", icon: PieChart },
              { id: "checklist", label: "Hiring Readiness", icon: ClipboardCheck },
              { id: "cost", label: "Hiring Cost Calculator", icon: BadgeDollarSign },
              { id: "planner", label: "Smart Hire Planner", icon: Brain },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => switchTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-[#1A1A1A] text-white"
                    : "bg-[#FAF9F6] text-gray-600 hover:bg-gray-100"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tool Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          {activeTab === "salary" && <SalaryCalculator />}
          {activeTab === "equity" && <EquityCalculator />}
          {activeTab === "checklist" && <HiringChecklist />}
          {activeTab === "cost" && <HiringCostCalculator />}
          {activeTab === "planner" && <SmartHirePlanner />}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <h2 className="font-display text-3xl text-white mb-4">Need help with your next hire?</h2>
          <p className="text-gray-400 mb-8">
            These tools are just the start. Let's talk about building your team.
          </p>
          <Link to="/for-startups" className="btn-luxury btn-luxury-gold">
            Start a Conversation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// ============ SALARY CALCULATOR ============
const SalaryCalculator = () => {
  const [inputs, setInputs] = useState({
    role: "",
    seniority: "",
    location: "",
    stage: "",
  });
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!inputs.role || !inputs.seniority || !inputs.location || !inputs.stage) return;

    const locInfo = salaryDataByLocation[inputs.location];
    const baseData = locInfo?.data?.[inputs.role]?.[inputs.seniority];
    const stageMult = stageMultipliers[inputs.stage];
    
    if (!baseData || !locInfo || !stageMult) return;

    // Salary is already in local currency - just apply stage multiplier
    const salaryLow = Math.round((baseData.base[0] * stageMult.salary) / 1000) * 1000;
    const salaryHigh = Math.round((baseData.base[1] * stageMult.salary) / 1000) * 1000;
    
    // Equity adjusted for stage
    const equityLow = (baseData.equity[0] * stageMult.equity).toFixed(2);
    const equityHigh = (baseData.equity[1] * stageMult.equity).toFixed(2);

    // Signing bonus: approximately 3-12% of low-end base salary, rounded to nearest 1000
    const signingLow = Math.round((salaryLow * 0.03) / 1000) * 1000;
    const signingHigh = Math.round((salaryHigh * 0.12) / 1000) * 1000;

    setResult({
      salaryLow,
      salaryHigh,
      equityLow,
      equityHigh,
      signingLow,
      signingHigh,
      currency: locInfo.currency,
      symbol: locInfo.symbol,
      flag: locInfo.flag,
      taxNote: locInfo.note || null,
    });
  };

  const formatCurrency = (value, symbol) => {
    return `${symbol}${value.toLocaleString()}`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <div>
        <h2 className="font-display text-2xl mb-2">Startup Salary Calculator</h2>
        <p className="text-body mb-8">
          Get realistic salary and equity benchmarks for VC-backed startups. Salaries displayed in local currency based on location.
        </p>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role Type *</label>
            <select
              value={inputs.role}
              onChange={(e) => setInputs({ ...inputs, role: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="salary-role"
            >
              <option value="">Select role</option>
              {ROLES.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Seniority Level *</label>
            <select
              value={inputs.seniority}
              onChange={(e) => setInputs({ ...inputs, seniority: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="salary-seniority"
            >
              <option value="">Select level</option>
              <option value="IC">Individual Contributor (IC)</option>
              <option value="Senior">Senior IC</option>
              <option value="Manager">Manager / Lead</option>
              <option value="Director">Director</option>
              <option value="VP">VP / Head of</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
            <select
              value={inputs.location}
              onChange={(e) => setInputs({ ...inputs, location: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="salary-location"
            >
              <option value="">Select country</option>
              <optgroup label="Americas">
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="Brazil">Brazil</option>
                <option value="Mexico">Mexico</option>
                <option value="Argentina">Argentina</option>
                <option value="Colombia">Colombia</option>
                <option value="Chile">Chile</option>
              </optgroup>
              <optgroup label="Europe">
                <option value="United Kingdom">United Kingdom</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Netherlands">Netherlands</option>
                <option value="Ireland">Ireland</option>
                <option value="Sweden">Sweden</option>
                <option value="Spain">Spain</option>
                <option value="Portugal">Portugal</option>
                <option value="Switzerland">Switzerland</option>
                <option value="Poland">Poland</option>
                <option value="Czech Republic">Czech Republic</option>
                <option value="Finland">Finland</option>
                <option value="Denmark">Denmark</option>
                <option value="Norway">Norway</option>
                <option value="Austria">Austria</option>
                <option value="Belgium">Belgium</option>
                <option value="Italy">Italy</option>
              </optgroup>
              <optgroup label="Middle East">
                <option value="UAE">UAE (Tax-free)</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Qatar">Qatar (Tax-free)</option>
                <option value="Bahrain">Bahrain (Tax-free)</option>
                <option value="Israel">Israel</option>
              </optgroup>
              <optgroup label="Asia-Pacific">
                <option value="India">India</option>
                <option value="Singapore">Singapore</option>
                <option value="Japan">Japan</option>
                <option value="South Korea">South Korea</option>
                <option value="China">China</option>
                <option value="Hong Kong">Hong Kong</option>
                <option value="Taiwan">Taiwan</option>
                <option value="Indonesia">Indonesia</option>
                <option value="Vietnam">Vietnam</option>
                <option value="Thailand">Thailand</option>
                <option value="Philippines">Philippines</option>
                <option value="Malaysia">Malaysia</option>
                <option value="Australia">Australia</option>
                <option value="New Zealand">New Zealand</option>
              </optgroup>
              <optgroup label="Africa">
                <option value="South Africa">South Africa</option>
                <option value="Nigeria">Nigeria</option>
                <option value="Kenya">Kenya</option>
                <option value="Egypt">Egypt</option>
              </optgroup>
              <optgroup label="Remote">
                <option value="Remote (US-based)">Remote (US-based)</option>
                <option value="Remote (Global)">Remote (Global)</option>
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Funding Stage *</label>
            <select
              value={inputs.stage}
              onChange={(e) => setInputs({ ...inputs, stage: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="salary-stage"
            >
              <option value="">Select stage</option>
              {Object.keys(stageMultipliers).map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>
          </div>

          <button
            onClick={calculate}
            disabled={!inputs.role || !inputs.seniority || !inputs.location || !inputs.stage}
            className="btn-luxury btn-luxury-dark w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="salary-calculate"
          >
            Calculate Salary Range
          </button>
        </div>

        {/* Methodology Note */}
        <div className="mt-8 p-4 bg-gray-50 border-l-4 border-gray-300">
          <p className="text-xs text-gray-500">
            <strong>Methodology:</strong> Salary data is sourced from Levels.fyi, Glassdoor, Carta, AngelList, GulfTalent, SeedLegals, and regional salary benchmarks (2024-2025). All figures are in local currency for the selected region. Stage multipliers adjust for startup maturity. Equity ranges reflect typical initial grants.
          </p>
        </div>
      </div>

      <div>
        {result ? (
          <div className="bg-white p-8 border border-gray-100" data-testid="salary-result">
            <div className="flex justify-end mb-2">
              <ShareResult hash="salary-calculator" summary={`${inputs.seniority !== "IC" ? inputs.seniority + " " : ""}${inputs.role} in ${inputs.location}: ${formatCurrency(result.salaryLow, result.symbol)}–${formatCurrency(result.salaryHigh, result.symbol)} base (${result.currency}).`} />
            </div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">{result.flag}</span>
              <p className="text-[#B8860B] text-xs tracking-wider uppercase">Estimated Compensation ({result.currency})</p>
            </div>
            <h3 className="font-display text-xl mb-2">
              {inputs.seniority === "IC" ? "" : inputs.seniority + " "}{inputs.role}
            </h3>
            <p className="text-sm text-gray-500 mb-8">
              {inputs.stage} • {inputs.location}
            </p>

            <div className="space-y-6">
              <div className="p-6 bg-[#FAF9F6]">
                <p className="text-sm text-gray-500 mb-1">Base Salary Range</p>
                <p className="font-display text-3xl">
                  {formatCurrency(result.salaryLow, result.symbol)} – {formatCurrency(result.salaryHigh, result.symbol)}
                </p>
                <p className="text-xs text-gray-400 mt-2">Annual base compensation, excluding bonuses</p>
                {result.taxNote && (
                  <p className="text-xs text-green-600 mt-2 font-medium">✓ {result.taxNote} – No income tax deductions</p>
                )}
              </div>

              <div className="p-6 bg-[#FAF9F6]">
                <p className="text-sm text-gray-500 mb-1">Typical Equity Grant</p>
                <p className="font-display text-3xl">
                  {result.equityLow}% – {result.equityHigh}%
                </p>
                <p className="text-xs text-gray-400 mt-2">Initial grant, typically vesting over 4 years with 1-year cliff</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">Typical Bonus</p>
                  <p className="font-medium">10-20% of base</p>
                </div>
                <div className="p-4 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">Signing Bonus</p>
                  <p className="font-medium">{formatCurrency(result.signingLow, result.symbol)}-{formatCurrency(result.signingHigh, result.symbol)}</p>
                </div>
              </div>

              {result.taxNote && (
                <div className="p-4 border-l-4 border-green-500 bg-green-50">
                  <p className="text-sm text-green-800">
                    <strong>UAE Tax Advantage:</strong> The UAE has no personal income tax, meaning the gross salary shown is effectively your take-home pay (before housing and other deductions). This can represent 20-40% more effective compensation compared to taxed jurisdictions.
                  </p>
                </div>
              )}

              <div className="p-4 border-l-4 border-[#B8860B] bg-[#FAF9F6]">
                <p className="text-sm text-gray-600">
                  <strong>Note:</strong> These are median ranges. Top-tier candidates or hot markets may command 15-25% premiums. Early employees (first 10-20) typically receive higher equity.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-[#FAF9F6] p-12 text-center h-full flex flex-col justify-center">
            <Calculator size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">Fill in the details to see salary benchmarks</p>
            <p className="text-xs text-gray-400">Results will display in local currency</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ EQUITY CALCULATOR ============
const EquityCalculator = () => {
  const [inputs, setInputs] = useState({
    equityPercent: "",
    valuation: "",
    stage: "",
  });
  const [result, setResult] = useState(null);

  const calculate = () => {
    const equity = parseFloat(inputs.equityPercent);
    const valuation = parseFloat(inputs.valuation) * 1000000;
    if (!equity || !valuation || !inputs.stage) return;

    const dilution = dilutionRates[inputs.stage];
    const postDilutionPercent = equity * (1 - dilution.toExit);

    // Calculate value at different exit scenarios
    const scenarios = [
      { exit: 50000000, label: "$50M", likelihood: "Acqui-hire / Small Exit" },
      { exit: 100000000, label: "$100M", likelihood: "Good Exit" },
      { exit: 250000000, label: "$250M", likelihood: "Strong Exit" },
      { exit: 500000000, label: "$500M", likelihood: "Excellent Exit" },
      { exit: 1000000000, label: "$1B", likelihood: "Unicorn Exit" },
    ];

    const values = scenarios.map((s) => ({
      ...s,
      value: Math.round((postDilutionPercent / 100) * s.exit),
      // Rough probability estimates
      probability: s.exit <= 100000000 ? "More likely" : s.exit >= 500000000 ? "Rare (<5%)" : "Possible",
    }));

    setResult({
      originalEquity: equity,
      postDilutionEquity: postDilutionPercent.toFixed(3),
      currentValue: Math.round((equity / 100) * valuation),
      exitValues: values,
      dilutionPercent: Math.round(dilution.toExit * 100),
      dilutionNote: dilution.rounds,
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <div>
        <h2 className="font-display text-2xl mb-2">Equity Calculator</h2>
        <p className="text-body mb-8">
          Understand what your startup equity could be worth at exit. Accounts for typical dilution from future funding rounds.
        </p>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Equity Stake (%) *</label>
            <input
              type="number"
              step="0.001"
              placeholder="e.g., 0.5 for half a percent"
              value={inputs.equityPercent}
              onChange={(e) => setInputs({ ...inputs, equityPercent: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="equity-percent"
            />
            <p className="text-xs text-gray-400 mt-1">Enter as percentage (0.5 = 0.5%, not 50%)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Company Valuation ($M) *</label>
            <input
              type="number"
              placeholder="e.g., 20 for $20 million"
              value={inputs.valuation}
              onChange={(e) => setInputs({ ...inputs, valuation: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="equity-valuation"
            />
            <p className="text-xs text-gray-400 mt-1">Post-money valuation from latest round</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Funding Stage *</label>
            <select
              value={inputs.stage}
              onChange={(e) => setInputs({ ...inputs, stage: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="equity-stage"
            >
              <option value="">Select stage</option>
              {Object.keys(dilutionRates).map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>
          </div>

          <button
            onClick={calculate}
            disabled={!inputs.equityPercent || !inputs.valuation || !inputs.stage}
            className="btn-luxury btn-luxury-dark w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="equity-calculate"
          >
            Calculate Equity Value
          </button>
        </div>

        {/* Risk Warning */}
        <div className="mt-8 p-4 bg-red-50 border-l-4 border-red-400">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-700">
              <p className="font-medium mb-1">Important Risk Factors</p>
              <ul className="space-y-1">
                <li>• ~90% of startups fail or return less than invested capital</li>
                <li>• Liquidation preferences may reduce common stock value to $0 in small exits</li>
                <li>• Your equity is illiquid until an exit event (IPO, acquisition)</li>
                <li>• Vesting schedules mean you don't own 100% immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div>
        {result ? (
          <div className="bg-white p-8 border border-gray-100" data-testid="equity-result">
            <div className="flex justify-end mb-2">
              <ShareResult hash="equity-calculator" summary={`${result.originalEquity}% equity at ${inputs.stage}: ~$${result.currentValue.toLocaleString()} today, ${result.postDilutionEquity}% at exit after dilution.`} />
            </div>
            <p className="text-[#B8860B] text-xs tracking-wider uppercase mb-4">Equity Analysis</p>
            <h3 className="font-display text-xl mb-2">{result.originalEquity}% Equity Stake</h3>
            <p className="text-sm text-gray-500 mb-6">{inputs.stage} • {result.dilutionNote}</p>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#FAF9F6]">
                  <p className="text-xs text-gray-500 mb-1">Current Paper Value</p>
                  <p className="font-display text-xl">${result.currentValue.toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-1">Before dilution</p>
                </div>
                <div className="p-4 bg-[#FAF9F6]">
                  <p className="text-xs text-gray-500 mb-1">Expected Dilution</p>
                  <p className="font-display text-xl">~{result.dilutionPercent}%</p>
                  <p className="text-xs text-gray-400 mt-1">By exit</p>
                </div>
              </div>

              <div className="p-4 bg-[#1A1A1A] text-white">
                <p className="text-xs text-gray-400 mb-1">Estimated Post-Dilution Ownership</p>
                <p className="font-display text-2xl">{result.postDilutionEquity}%</p>
                <p className="text-xs text-gray-400 mt-1">Your stake at exit after future funding rounds</p>
              </div>

              <div>
                <p className="text-sm font-medium mb-3">Potential Value at Exit</p>
                <div className="space-y-2">
                  {result.exitValues.map((scenario, i) => (
                    <div key={i} className="flex justify-between items-center p-3 bg-[#FAF9F6]">
                      <div>
                        <span className="text-sm text-gray-600">{scenario.label} exit</span>
                        <span className="text-xs text-gray-400 ml-2">({scenario.probability})</span>
                      </div>
                      <span className="font-display text-lg">${scenario.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 border-l-4 border-[#B8860B] bg-[#FAF9F6]">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>What this doesn't include:</strong>
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>• <strong>Liquidation preferences:</strong> Investors get paid first. In a $50M exit with $40M invested, common shareholders may get very little.</li>
                  <li>• <strong>Taxes:</strong> Capital gains, AMT on ISO exercise, etc. can reduce take-home by 20-40%.</li>
                  <li>• <strong>Vesting:</strong> If you leave before fully vested, you forfeit unvested shares.</li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-[#FAF9F6] p-12 text-center h-full flex flex-col justify-center">
            <PieChart size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">Enter your equity details to see potential value</p>
            <p className="text-xs text-gray-400">Includes dilution estimates and exit scenarios</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ HIRING CHECKLIST ============
const HiringChecklist = () => {
  const [selectedRole, setSelectedRole] = useState("");
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const checklists = {
    "First Engineer": [
      { id: 1, question: "Do you have a clear technical vision or product roadmap?", weight: 2, tip: "Engineers want to know what they'll be building" },
      { id: 2, question: "Have you defined your tech stack (or are you open to letting them choose)?", weight: 1, tip: "Strong engineers often prefer greenfield decisions" },
      { id: 3, question: "Do you have at least 12-18 months of runway?", weight: 2, tip: "Top talent avoids companies that might run out of money" },
      { id: 4, question: "Can you articulate what 'success' looks like at 30, 60, and 90 days?", weight: 2, tip: "Clear expectations attract high performers" },
      { id: 5, question: "Do you know your total compensation budget (salary + equity)?", weight: 1, tip: "Market rates for senior engineers: $150-200K+ in SF, $100-150K in London" },
      { id: 6, question: "Have you written a job description focused on impact, not requirements?", weight: 1, tip: "Best JDs describe the problems to solve, not checkbox requirements" },
      { id: 7, question: "Do you have a technical co-founder or advisor who can assess candidates?", weight: 2, tip: "Non-technical founders often mis-hire; get expert help" },
      { id: 8, question: "Are you prepared to spend 15-20+ hours/week on hiring for the next 4-8 weeks?", weight: 1, tip: "Hiring is a full-time job; don't underestimate the commitment" },
    ],
    "First Product Manager": [
      { id: 1, question: "Do you have product-market fit (or clear signals you're close)?", weight: 2, tip: "A PM before PMF often struggles to add value" },
      { id: 2, question: "Is the founder genuinely ready to delegate product decisions?", weight: 2, tip: "Many founder-PMs fail because the founder won't let go" },
      { id: 3, question: "Do you have at least 3-4 engineers for a PM to work with?", weight: 2, tip: "A PM with no engineering capacity can't ship" },
      { id: 4, question: "Have you clearly defined the PM scope (strategy vs. execution)?", weight: 1, tip: "Early PMs often need to be more hands-on executional" },
      { id: 5, question: "Do you know your compensation budget?", weight: 1, tip: "Senior PMs: $140-180K+ in SF, $90-130K in London" },
      { id: 6, question: "Can you articulate what 'success' looks like in the first 90 days?", weight: 2, tip: "Shipping a feature? Defining roadmap? Reducing churn?" },
      { id: 7, question: "Do you have customer feedback loops and data infrastructure?", weight: 1, tip: "PMs need data and customer access to make good decisions" },
      { id: 8, question: "Is there a clear reporting structure for this role?", weight: 1, tip: "First PM often reports to CEO; make this explicit" },
    ],
    "First Sales Hire": [
      { id: 1, question: "Have founders closed at least 10-20 customers themselves?", weight: 2, tip: "You need to understand the sale before hiring someone to scale it" },
      { id: 2, question: "Do you have a repeatable sales motion (even if informal)?", weight: 2, tip: "First salesperson should scale a process, not invent one" },
      { id: 3, question: "Is your pricing model established and validated?", weight: 2, tip: "Sales reps need clear pricing to close deals" },
      { id: 4, question: "Do you have inbound leads or pipeline for a new rep to work?", weight: 2, tip: "Cold-start is hard; warm leads accelerate ramp time" },
      { id: 5, question: "Do you know your average deal size and sales cycle?", weight: 1, tip: "This determines whether you need an SDR, AE, or hybrid" },
      { id: 6, question: "Do you have a CRM or way to track pipeline?", weight: 1, tip: "Even a spreadsheet works early; just have something" },
      { id: 7, question: "Do you know your compensation structure (base + OTE)?", weight: 1, tip: "50/50 split is common; OTE should be achievable" },
      { id: 8, question: "Have you defined quota expectations for month 3, 6, 12?", weight: 1, tip: "Be realistic; most reps take 3-6 months to ramp" },
    ],
  };

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const calculateScore = () => {
    if (!selectedRole) return { score: 0, max: 0, percent: 0 };
    const questions = checklists[selectedRole];
    let score = 0;
    let maxScore = 0;

    questions.forEach((q) => {
      maxScore += q.weight;
      if (answers[q.id] === true) {
        score += q.weight;
      }
    });

    return {
      score,
      max: maxScore,
      percent: Math.round((score / maxScore) * 100),
    };
  };

  const getRecommendation = (percent) => {
    if (percent >= 80) {
      return {
        status: "Ready to Hire",
        color: "text-green-600",
        bgColor: "bg-green-500",
        message: "You're well-prepared to start this search. The foundations are in place for a successful hire.",
      };
    } else if (percent >= 50) {
      return {
        status: "Almost Ready",
        color: "text-[#B8860B]",
        bgColor: "bg-[#B8860B]",
        message: "You're on the right track, but addressing the gaps below will significantly improve your hiring outcome and reduce mis-hire risk.",
      };
    } else {
      return {
        status: "Not Ready Yet",
        color: "text-red-600",
        bgColor: "bg-red-500",
        message: "Consider addressing the unchecked items before starting your search. Hiring before you're ready often leads to expensive mis-hires and wasted time.",
      };
    }
  };

  const result = calculateScore();
  const recommendation = getRecommendation(result.percent);
  const allAnswered = selectedRole && Object.keys(answers).length === checklists[selectedRole]?.length;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="font-display text-2xl mb-2">First Hire Readiness Checklist</h2>
        <p className="text-body">
          Are you ready to make your first key hire? Answer these questions honestly to find out.
        </p>
      </div>

      {/* Role Selection */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-3">What role are you hiring for?</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.keys(checklists).map((role) => (
            <button
              key={role}
              onClick={() => {
                setSelectedRole(role);
                setAnswers({});
                setShowResult(false);
              }}
              className={`p-4 border text-left transition-all ${
                selectedRole === role
                  ? "border-[#B8860B] bg-[#B8860B]/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`checklist-role-${role.replace(/\s/g, "-").toLowerCase()}`}
            >
              <span className="font-medium">{role}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Questions */}
      {selectedRole && (
        <div className="space-y-4 mb-8">
          {checklists[selectedRole].map((q, i) => (
            <div
              key={q.id}
              className={`p-5 border transition-all ${
                answers[q.id] === true
                  ? "border-green-200 bg-green-50"
                  : answers[q.id] === false
                  ? "border-red-200 bg-red-50"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-gray-800 mb-1">{q.question}</p>
                  <p className="text-xs text-gray-500">{q.tip}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleAnswer(q.id, true)}
                    className={`p-2 rounded-full transition-all ${
                      answers[q.id] === true
                        ? "bg-green-500 text-white"
                        : "bg-gray-100 text-gray-400 hover:bg-green-100 hover:text-green-600"
                    }`}
                    data-testid={`checklist-yes-${q.id}`}
                  >
                    <Check size={18} />
                  </button>
                  <button
                    onClick={() => handleAnswer(q.id, false)}
                    className={`p-2 rounded-full transition-all ${
                      answers[q.id] === false
                        ? "bg-red-500 text-white"
                        : "bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-600"
                    }`}
                    data-testid={`checklist-no-${q.id}`}
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Result */}
      {selectedRole && allAnswered && (
        <div className="bg-white p-8 border border-gray-100" data-testid="checklist-result">
          <div className="flex justify-end mb-2">
            <ShareResult hash="hiring-readiness" summary={`Hiring readiness score: ${result.percent}% — ${recommendation.status}.`} />
          </div>
          <div className="text-center mb-6">
            <p className="text-[#B8860B] text-xs tracking-wider uppercase mb-2">Your Readiness Score</p>
            <p className="font-display text-5xl mb-2">{result.percent}%</p>
            <p className={`font-medium text-lg ${recommendation.color}`}>{recommendation.status}</p>
          </div>

          <div className="w-full bg-gray-200 h-2 mb-6">
            <div
              className={`h-full transition-all ${recommendation.bgColor}`}
              style={{ width: `${result.percent}%` }}
            />
          </div>

          <p className="text-body text-center mb-8">{recommendation.message}</p>

          {result.percent < 100 && (
            <div className="border-t border-gray-100 pt-6">
              <p className="text-sm font-medium mb-3">Areas to address before hiring:</p>
              <ul className="space-y-3">
                {checklists[selectedRole]
                  .filter((q) => answers[q.id] === false)
                  .map((q) => (
                    <li key={q.id} className="flex items-start gap-2 text-sm">
                      <X size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-gray-800">{q.question}</span>
                        <p className="text-xs text-gray-500 mt-0.5">{q.tip}</p>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500 mb-4">Want help preparing for this hire?</p>
            <Link to="/contact" className="btn-luxury btn-luxury-outline">
              Book a Free Consultation
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}

      {/* Methodology Note */}
      {selectedRole && (
        <div className="mt-8 p-4 bg-gray-50 border-l-4 border-gray-300">
          <p className="text-xs text-gray-500">
            <strong>About this checklist:</strong> Questions are weighted by importance. Core readiness factors (like runway and ability to assess candidates) are weighted 2x. This framework is based on patterns from 100+ startup hiring processes.
          </p>
        </div>
      )}
    </div>
  );
};

// ============ HIRING COST CALCULATOR ============
const HiringCostCalculator = () => {
  const [inputs, setInputs] = useState({
    salary: "",
    currency: "USD",
    numHires: 1,
    monthsRetainer: 3,
  });
  const [result, setResult] = useState(null);

  const calculate = () => {
    const salary = parseFloat(inputs.salary);
    if (!salary || salary <= 0) return;

    const numHires = Math.max(1, parseInt(inputs.numHires) || 1);
    const months = Math.max(1, parseInt(inputs.monthsRetainer) || 3);
    const sym = currencyOptions[inputs.currency].symbol;

    // Priority Search: 25% total (5% upfront + 20% on placement)
    const priorityPerHire = salary * 0.25;
    const priorityUpfront = salary * 0.05;
    const priorityOnPlacement = salary * 0.20;
    const priorityTotal = priorityPerHire * numHires;

    // Partnership Search: 20% (no upfront)
    const partnershipPerHire = salary * 0.20;
    const partnershipTotal = partnershipPerHire * numHires;

    // Growth Partner: $500/mo retainer + 15% success fee
    const usdToLocal = currencyOptions[inputs.currency].usdToLocal;
    const retainerMonthlyUSD = 500;
    const retainerMonthlyLocal = Math.round(retainerMonthlyUSD * usdToLocal);
    const retainerTotal = retainerMonthlyLocal * months;
    const growthSuccessFee = salary * 0.15;
    const growthPerHire = growthSuccessFee;
    const growthTotal = retainerTotal + (growthSuccessFee * numHires);

    // Savings
    const savingsVsPriority = priorityTotal - growthTotal;
    const savingsVsPartnership = partnershipTotal - growthTotal;
    const savingsPercentVsPriority = priorityTotal > 0 ? Math.round((savingsVsPriority / priorityTotal) * 100) : 0;

    // Best value determination
    let bestValue = "partnership";
    if (growthTotal <= partnershipTotal && growthTotal <= priorityTotal) bestValue = "growth";
    else if (priorityTotal <= partnershipTotal) bestValue = "priority";

    setResult({
      salary,
      numHires,
      months,
      sym,
      priority: {
        upfront: priorityUpfront,
        onPlacement: priorityOnPlacement,
        perHire: priorityPerHire,
        total: priorityTotal,
      },
      partnership: {
        perHire: partnershipPerHire,
        total: partnershipTotal,
      },
      growth: {
        retainerMonthly: retainerMonthlyLocal,
        retainerTotal,
        successFee: growthSuccessFee,
        perHire: growthPerHire,
        total: growthTotal,
      },
      savingsVsPriority,
      savingsPercentVsPriority,
      savingsVsPartnership,
      bestValue,
    });
  };

  const fmt = (value, symbol) => `${symbol}${Math.round(value).toLocaleString()}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <div>
        <h2 className="font-display text-2xl mb-2">Hiring Cost Calculator</h2>
        <p className="text-body mb-8">
          Compare the total cost of our three engagement models side by side. See which one works best for your hiring plan.
        </p>

        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">CTC (Cost to Company) for the Role *</label>
              <input
                type="number"
                placeholder="e.g., 80000"
                value={inputs.salary}
                onChange={(e) => setInputs({ ...inputs, salary: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
                data-testid="cost-salary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
              <select
                value={inputs.currency}
                onChange={(e) => setInputs({ ...inputs, currency: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
                data-testid="cost-currency"
              >
                {Object.entries(currencyOptions).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Number of Hires Planned *</label>
            <input
              type="number"
              min="1"
              max="50"
              value={inputs.numHires}
              onChange={(e) => setInputs({ ...inputs, numHires: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="cost-num-hires"
            />
            <p className="text-xs text-gray-400 mt-1">Total number of hires you expect to make</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Growth Partner Duration (months)</label>
            <select
              value={inputs.monthsRetainer}
              onChange={(e) => setInputs({ ...inputs, monthsRetainer: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="cost-months"
            >
              {[3, 6, 9, 12].map((m) => (
                <option key={m} value={m}>{m} months</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">Duration of the Growth Partner retainer (~$500/month, converted to selected currency)</p>
          </div>

          <button
            onClick={calculate}
            disabled={!inputs.salary || parseFloat(inputs.salary) <= 0}
            className="btn-luxury btn-luxury-dark w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="cost-calculate"
          >
            Compare Costs
          </button>
        </div>

        <div className="mt-8 p-4 bg-gray-50 border-l-4 border-gray-300">
          <p className="text-xs text-gray-500">
            <strong>Note:</strong> Growth Partner retainer is $500/month (converted to your selected currency at approximate pegged/market rates). Success fees are calculated as a percentage of the role's CTC (Cost to Company). All figures assume same salary per role.
          </p>
        </div>

        <div className="mt-4 p-4 bg-amber-50 border-l-4 border-[#B8860B]">
          <div className="flex items-start gap-2">
            <Info size={14} className="text-[#B8860B] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-700">Disclaimer</p>
              <p>This calculator is for <strong>illustrative purposes only</strong> and provides approximate cost estimates based on standard fee structures. Actual engagement fees may vary depending on role complexity, seniority, market conditions, and scope of search.</p>
              <p>All fees are subject to the terms of a signed engagement agreement. The 5% upfront engagement fee (Priority Search) is fully refundable within 10 business days of engagement; a 1% administrative charge applies thereafter. Replacement guarantees are subject to the terms outlined in the service agreement.</p>
              <p>For a tailored quote, please <Link to="/contact" className="text-[#B8860B] underline hover:no-underline">contact us directly</Link>.</p>
            </div>
          </div>
        </div>
      </div>

      <div>
        {result ? (
          <div className="space-y-4" data-testid="cost-result">
            <div className="flex justify-end">
              <ShareResult hash="hiring-cost-calculator" summary={`${result.numHires} hire${result.numHires > 1 ? "s" : ""} at ${fmt(result.salary, result.sym)} CTC — Growth Partner total ${fmt(result.growth.total, result.sym)}.`} />
            </div>
            <div className="bg-white p-6 border border-gray-100">
              <p className="text-[#B8860B] text-xs tracking-wider uppercase mb-4">Cost Comparison</p>
              <p className="text-sm text-gray-500 mb-6">
                {result.numHires} hire{result.numHires > 1 ? "s" : ""} at {fmt(result.salary, result.sym)} CTC
              </p>

              {/* Priority Search */}
              <div className={`p-5 mb-3 border ${result.bestValue === "priority" ? "border-[#B8860B] bg-[#B8860B]/5" : "border-gray-100"}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Zap size={16} className="text-[#B8860B]" />
                      <h4 className="font-display text-lg">Priority Search</h4>
                      {result.bestValue === "priority" && <span className="text-xs bg-[#B8860B] text-white px-2 py-0.5">BEST VALUE</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">25% of CTC</p>
                  </div>
                  <p className="font-display text-2xl">{fmt(result.priority.total, result.sym)}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">Upfront (5%)</p>
                    <p className="font-medium">{fmt(result.priority.upfront, result.sym)} per hire</p>
                  </div>
                  <div className="bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">On Placement (20%)</p>
                    <p className="font-medium">{fmt(result.priority.onPlacement, result.sym)} per hire</p>
                  </div>
                </div>
              </div>

              {/* Partnership Search */}
              <div className={`p-5 mb-3 border ${result.bestValue === "partnership" ? "border-[#B8860B] bg-[#B8860B]/5" : "border-gray-100"}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Shield size={16} className="text-gray-400" />
                      <h4 className="font-display text-lg">Partnership Search</h4>
                      {result.bestValue === "partnership" && <span className="text-xs bg-[#B8860B] text-white px-2 py-0.5">BEST VALUE</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">20% of CTC, no upfront</p>
                  </div>
                  <p className="font-display text-2xl">{fmt(result.partnership.total, result.sym)}</p>
                </div>
                <div className="bg-gray-50 p-3 text-sm">
                  <p className="text-xs text-gray-500">Per Hire</p>
                  <p className="font-medium">{fmt(result.partnership.perHire, result.sym)}</p>
                </div>
              </div>

              {/* Growth Partner */}
              <div className={`p-5 border-2 ${result.bestValue === "growth" ? "border-[#B8860B] bg-[#B8860B]/5" : "border-gray-200"}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Star size={16} className="text-[#B8860B]" />
                      <h4 className="font-display text-lg">Growth Partner</h4>
                      {result.bestValue === "growth" && <span className="text-xs bg-[#B8860B] text-white px-2 py-0.5">BEST VALUE</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{result.sym}{Math.round(result.growth.retainerMonthly).toLocaleString()}/mo + 15% success fee</p>
                  </div>
                  <p className="font-display text-2xl">{fmt(result.growth.total, result.sym)}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">Retainer ({result.months}mo)</p>
                    <p className="font-medium">{fmt(result.growth.retainerTotal, result.sym)}</p>
                  </div>
                  <div className="bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">Success Fees (15%)</p>
                    <p className="font-medium">{fmt(result.growth.successFee * result.numHires, result.sym)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Savings summary */}
            {result.savingsVsPriority > 0 && result.bestValue === "growth" && (
              <div className="bg-green-50 p-5 border border-green-200">
                <p className="text-sm font-medium text-green-800 mb-2">Growth Partner Savings</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-green-600">vs Priority Search</p>
                    <p className="font-display text-xl text-green-800">{fmt(result.savingsVsPriority, result.sym)}</p>
                    <p className="text-xs text-green-600">({result.savingsPercentVsPriority}% less)</p>
                  </div>
                  {result.savingsVsPartnership > 0 && (
                    <div>
                      <p className="text-xs text-green-600">vs Partnership Search</p>
                      <p className="font-display text-xl text-green-800">{fmt(result.savingsVsPartnership, result.sym)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Insight */}
            <div className="p-5 bg-[#1A1A1A] text-white">
              <p className="text-xs text-[#B8860B] uppercase tracking-wider mb-2">Recommendation</p>
              <p className="text-sm text-gray-300">
                {result.bestValue === "growth"
                  ? `With ${result.numHires} hire${result.numHires > 1 ? "s" : ""} over ${result.months} months, Growth Partner offers the best value. The monthly retainer is offset by the lower 15% success fee.`
                  : result.bestValue === "partnership"
                  ? `For ${result.numHires} hire${result.numHires > 1 ? "s" : ""}, Partnership Search gives you the lowest total cost with zero upfront commitment.`
                  : `Priority Search, while the highest fee, gives you the fastest time-to-hire with a dedicated search team. Best when speed matters most.`
                }
              </p>
            </div>

            <div className="text-center pt-4">
              <Link to="/for-startups#contact-form" className="btn-luxury btn-luxury-gold">
                Start a Conversation
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-[#FAF9F6] p-12 text-center h-full flex flex-col justify-center">
            <BadgeDollarSign size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">Enter salary details to compare engagement costs</p>
            <p className="text-xs text-gray-400">See which model is the best value for your hiring plan</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ SMART HIRE PLANNER ============
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SmartHirePlanner = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [inputs, setInputs] = useState({
    business_description: "",
    funding_stage: "",
    monthly_revenue: "",
    team_size: "",
    primary_goal: "",
    bottleneck: "",
    location: "",
    budget: "",
    target_role: "",
    work_mode: "",
    company_priorities: [],
    culture_traits: [],
    team_gaps: "",
  });

  const update = (key, val) => setInputs({ ...inputs, [key]: val });

  const toggleMulti = (key, val) => {
    const arr = inputs[key] || [];
    if (arr.includes(val)) {
      setInputs({ ...inputs, [key]: arr.filter(v => v !== val) });
    } else {
      setInputs({ ...inputs, [key]: [...arr, val] });
    }
  };

  const fundingStages = ["Pre-Seed / Seed", "Series A", "Series B", "Series C+"];
  const revenueOptions = ["$0 (pre-revenue)", "< $10K/mo", "$10-50K/mo", "$50-200K/mo", "$200K+/mo"];
  const teamSizes = ["Just me", "2-5 people", "6-15 people", "16-30 people", "30+ people"];
  const goals = ["Build MVP / Product", "Launch & get first customers", "Scale revenue", "Expand to new markets", "Raise next round"];
  const bottlenecks = ["Can't build fast enough", "Can't sell or market effectively", "Can't retain customers", "Can't manage operations / too many hats", "Not sure — need help figuring it out"];
  const workModes = ["Full-time", "Part-time", "Contractor", "Remote"];
  const priorityOptions = ["Product Validation", "Revenue Growth", "Process Optimization", "Scaling Team", "Fundraising", "Market Expansion"];
  const cultureOptions = ["Scrappy / Resourceful", "Data-Driven", "Move Fast", "Deep Expertise", "Collaborative", "Autonomous", "Customer-Obsessed", "Mission-Driven"];
  const locations = [
    "San Francisco Bay Area", "New York City", "Seattle / Austin / Denver", "Other US Cities",
    "London", "Other UK Cities", "Dubai", "Abu Dhabi", "Other UAE",
    "Saudi Arabia (Riyadh)", "Berlin / Amsterdam", "Other Europe",
    "Remote (US-based)", "Remote (Global)",
  ];
  const budgets = ["< $5K/mo", "$5-10K/mo", "$10-20K/mo", "$20-40K/mo", "$40K+/mo", "Flexible / not sure"];

  const canProceed = () => {
    if (step === 1) return inputs.business_description.length >= 10 && inputs.funding_stage && inputs.monthly_revenue && inputs.team_size;
    if (step === 2) return inputs.primary_goal && inputs.bottleneck && inputs.location && inputs.budget;
    return false;
  };

  const generatePlan = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/tools/smart-hire-planner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(inputs),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail || "";
        if (detail.toLowerCase().includes("budget")) {
          throw new Error("budget");
        } else if (detail.toLowerCase().includes("502") || detail.toLowerCase().includes("gateway")) {
          throw new Error("timeout");
        }
        throw new Error(detail || "unknown");
      }
      const data = await res.json();
      setResult(data.data);
      setStep(3);
    } catch (err) {
      if (err.message === "budget") {
        setError("Your AI credits have been exhausted. Please go to Profile > Universal Key > Add Balance to continue using the Smart Hire Planner.");
      } else if (err.message === "timeout") {
        setError("The AI service is temporarily busy. Please wait a moment and try again.");
      } else {
        setError("Something went wrong. Please try again in a few moments.");
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/api/tools/smart-hire-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: result, inputs }),
      });
      if (!res.ok) throw new Error("Failed to generate report");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "MK_Smart_Hire_Plan.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download report.");
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (val, sym) => {
    try { return `${sym || "$"}${Math.round(val).toLocaleString()}`; } catch { return String(val); }
  };

  const OptionButton = ({ label, selected, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 text-sm text-left border transition-all ${
        selected ? "border-[#B8860B] bg-[#B8860B]/5 text-[#1A1A1A] font-medium" : "border-gray-200 text-gray-600 hover:border-gray-300"
      }`}
    >
      {label}
    </button>
  );

  // Step 1: Business Context
  if (step === 1) {
    return (
      <div className="max-w-2xl">
        <h2 className="font-display text-2xl mb-2">Smart Hire Planner</h2>
        <p className="text-body mb-8">Tell us about your business. We'll tell you who to hire, when, and at what cost.</p>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">What does your company do? *</label>
            <textarea
              value={inputs.business_description}
              onChange={(e) => update("business_description", e.target.value)}
              placeholder="e.g., B2B SaaS platform for HR automation, helping mid-market companies streamline payroll and compliance..."
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none min-h-[80px] resize-none"
              data-testid="planner-description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Funding stage *</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {fundingStages.map((s) => (
                <OptionButton key={s} label={s} selected={inputs.funding_stage === s} onClick={() => update("funding_stage", s)} />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current monthly revenue *</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {revenueOptions.map((r) => (
                <OptionButton key={r} label={r} selected={inputs.monthly_revenue === r} onClick={() => update("monthly_revenue", r)} />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current team size *</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {teamSizes.map((t) => (
                <OptionButton key={t} label={t} selected={inputs.team_size === t} onClick={() => update("team_size", t)} />
              ))}
            </div>
          </div>

          <button
            onClick={() => setStep(2)}
            disabled={!canProceed()}
            className="btn-luxury btn-luxury-dark w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="planner-next-1"
          >
            Continue <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Goals & Constraints
  if (step === 2) {
    return (
      <div className="max-w-2xl">
        <button onClick={() => setStep(1)} className="text-sm text-gray-400 hover:text-gray-600 mb-4">&larr; Back</button>
        <h2 className="font-display text-2xl mb-2">Goals & Context</h2>
        <p className="text-body mb-8">Help us understand your priorities so we can recommend the right hires.</p>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Role (optional — leave blank for general advice)</label>
            <input
              type="text"
              value={inputs.target_role}
              onChange={(e) => update("target_role", e.target.value)}
              placeholder="e.g., Senior Product Manager, Full-Stack Engineer, Head of Sales..."
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none"
              data-testid="planner-target-role"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Primary goal for the next 12 months *</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {goals.map((g) => (
                <OptionButton key={g} label={g} selected={inputs.primary_goal === g} onClick={() => update("primary_goal", g)} />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Biggest bottleneck right now *</label>
            <div className="grid grid-cols-1 gap-2">
              {bottlenecks.map((b) => (
                <OptionButton key={b} label={b} selected={inputs.bottleneck === b} onClick={() => update("bottleneck", b)} />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Priorities (select all that apply)</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {priorityOptions.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggleMulti("company_priorities", p)}
                  className={`px-3 py-2 text-sm text-left border transition-all ${
                    inputs.company_priorities.includes(p) ? "border-[#B8860B] bg-[#B8860B]/5 text-[#1A1A1A] font-medium" : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid={`priority-${p.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {inputs.company_priorities.includes(p) && <span className="mr-1 text-[#B8860B]">&#10003;</span>}
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Work Mode</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {workModes.map((w) => (
                <OptionButton key={w} label={w} selected={inputs.work_mode === w} onClick={() => update("work_mode", w)} />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Where will you hire? *</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {locations.map((l) => (
                <OptionButton key={l} label={l} selected={inputs.location === l} onClick={() => update("location", l)} />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Monthly budget for new hires *</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {budgets.map((b) => (
                <OptionButton key={b} label={b} selected={inputs.budget === b} onClick={() => update("budget", b)} />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Culture Traits You Value (optional)</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {cultureOptions.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleMulti("culture_traits", c)}
                  className={`px-3 py-2 text-xs text-left border transition-all ${
                    inputs.culture_traits.includes(c) ? "border-[#B8860B] bg-[#B8860B]/5 text-[#1A1A1A] font-medium" : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid={`culture-${c.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {inputs.culture_traits.includes(c) && <span className="mr-1 text-[#B8860B]">&#10003;</span>}
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Team Gaps (optional)</label>
            <textarea
              value={inputs.team_gaps}
              onChange={(e) => update("team_gaps", e.target.value)}
              placeholder="e.g., We have 3 engineers but no one focused on infrastructure. Marketing is handled by the CEO part-time..."
              className="w-full px-4 py-3 border border-gray-200 bg-white focus:border-[#B8860B] focus:outline-none min-h-[60px] resize-none"
              data-testid="planner-team-gaps"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
          )}

          <button
            onClick={generatePlan}
            disabled={!canProceed() || loading}
            className="btn-luxury btn-luxury-dark w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="planner-generate"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2"><Loader2 size={18} className="animate-spin" /> Analyzing your business (30-45s)...</span>
            ) : (
              <span className="flex items-center justify-center gap-2"><Brain size={18} /> Generate My Hiring Plan</span>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Step 3: Results
  if (step === 3 && result) {
    const sym = result.currency_symbol || "$";
    const score = result.stage_fit_score || 0;
    const scoreColor = score >= 70 ? "text-green-600" : score >= 50 ? "text-[#B8860B]" : "text-red-500";
    const scoreBg = score >= 70 ? "bg-green-50 border-green-200" : score >= 50 ? "bg-[#B8860B]/5 border-[#B8860B]/20" : "bg-red-50 border-red-200";
    const scoreLabel = score >= 90 ? "Excellent Fit" : score >= 70 ? "Good Fit" : score >= 50 ? "Moderate Fit" : "Low Fit";

    return (
      <div data-testid="planner-result">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display text-2xl mb-2">Your Smart Hire Plan</h2>
            <p className="text-body text-sm">{result.summary}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => { setStep(1); setResult(null); }} className="px-4 py-2 text-sm border border-gray-200 hover:border-gray-400 transition-colors">
              Start Over
            </button>
            <button
              onClick={downloadReport}
              disabled={downloading}
              className="btn-luxury btn-luxury-gold flex items-center gap-2"
              data-testid="planner-download"
            >
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Download PDF Report
            </button>
          </div>
        </div>

        {/* Stage-Fit Score + Key Numbers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className={`p-5 text-center border ${scoreBg}`} data-testid="stage-fit-score">
            <p className="text-xs text-gray-500 tracking-wider uppercase mb-1">Stage-Fit Score</p>
            <p className={`font-display text-4xl ${scoreColor}`}>{score}</p>
            <p className={`text-xs font-medium mt-1 ${scoreColor}`}>{scoreLabel}</p>
          </div>
          <div className="bg-[#FAF9F6] p-5 text-center">
            <p className="text-xs text-[#B8860B] tracking-wider uppercase mb-1">Recommended Hires</p>
            <p className="font-display text-3xl">{result.recommended_hires?.length || 0}</p>
          </div>
          <div className="bg-[#FAF9F6] p-5 text-center">
            <p className="text-xs text-[#B8860B] tracking-wider uppercase mb-1">Monthly Cost</p>
            <p className="font-display text-3xl">{fmt(result.total_monthly_cost, sym)}</p>
          </div>
          <div className="bg-[#FAF9F6] p-5 text-center">
            <p className="text-xs text-[#B8860B] tracking-wider uppercase mb-1">Annual Investment</p>
            <p className="font-display text-3xl">{fmt(result.total_annual_cost, sym)}</p>
          </div>
        </div>

        {/* Stage Fit Rationale */}
        {result.stage_fit_rationale && (
          <div className={`p-4 mb-6 border-l-4 ${score >= 70 ? "border-green-500 bg-green-50" : score >= 50 ? "border-[#B8860B] bg-[#B8860B]/5" : "border-red-400 bg-red-50"}`}>
            <p className="text-xs font-medium text-gray-500 uppercase mb-1">Stage-Fit Analysis</p>
            <p className="text-sm text-gray-700">{result.stage_fit_rationale}</p>
          </div>
        )}

        {/* Affordability — budget & runway awareness */}
        {result.affordability && (() => {
          const util = Number(result.affordability.budget_utilization_pct) || 0;
          const verdict = (result.affordability.verdict || "").toLowerCase();
          const over = verdict.includes("over") || util > 100;
          const stretch = verdict.includes("stretch") || (util > 75 && util <= 100);
          const barColor = over ? "bg-red-500" : stretch ? "bg-[#B8860B]" : "bg-green-500";
          const box = over ? "border-red-200 bg-red-50" : stretch ? "border-[#B8860B]/20 bg-[#B8860B]/5" : "border-green-200 bg-green-50";
          const vColor = over ? "text-red-600" : stretch ? "text-[#B8860B]" : "text-green-600";
          return (
            <div className={`p-5 mb-6 border ${box}`} data-testid="affordability-card">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Budget & Runway Check</p>
                <span className={`text-sm font-medium ${vColor}`}>{result.affordability.verdict}</span>
              </div>
              {util > 0 && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Budget used</span>
                    <span>{util}%</span>
                  </div>
                  <div className="w-full bg-gray-200 h-2">
                    <div className={`h-full transition-all ${barColor}`} style={{ width: `${Math.min(util, 100)}%` }} />
                  </div>
                </div>
              )}
              {result.affordability.monthly_cost_vs_budget && (
                <p className="text-sm text-gray-700 mb-2">{result.affordability.monthly_cost_vs_budget}</p>
              )}
              {result.affordability.runway_impact && (
                <p className="text-sm text-gray-600 mb-2"><strong className="text-gray-700">Runway:</strong> {result.affordability.runway_impact}</p>
              )}
              {result.affordability.recommendation && (
                <p className="text-sm text-gray-700 pt-2 border-t border-gray-200/60"><strong className={vColor}>Recommendation:</strong> {result.affordability.recommendation}</p>
              )}
            </div>
          );
        })()}

        {/* Cost of Delay — the reason to act */}
        {result.cost_of_delay && (
          <div className="p-5 mb-6 border-l-4 border-[#B8860B] bg-white shadow-sm" data-testid="cost-of-delay-card">
            <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-3">The Cost of Waiting</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              {result.cost_of_delay.monthly_cost_of_inaction && (
                <div className="p-3 bg-[#FAF9F6]">
                  <p className="text-xs text-gray-500 mb-1">Cost of the gap staying open</p>
                  <p className="text-sm text-gray-800">{result.cost_of_delay.monthly_cost_of_inaction}</p>
                </div>
              )}
              {result.cost_of_delay.cost_of_bad_hire && (
                <div className="p-3 bg-[#FAF9F6]">
                  <p className="text-xs text-gray-500 mb-1">Cost of a mis-hire</p>
                  <p className="text-sm text-gray-800">{result.cost_of_delay.cost_of_bad_hire}</p>
                </div>
              )}
            </div>
            {result.cost_of_delay.urgency_narrative && (
              <p className="text-sm text-gray-700">{result.cost_of_delay.urgency_narrative}</p>
            )}
          </div>
        )}

        {/* Key Insight */}
        {result.key_insight && (
          <div className="p-5 bg-[#1A1A1A] text-white mb-8">
            <p className="text-xs text-[#B8860B] uppercase tracking-wider mb-2">Key Insight</p>
            <p className="text-sm text-gray-300">{result.key_insight}</p>
          </div>
        )}

        {/* Tabs for different sections */}
        <ResultTabs result={result} sym={sym} fmt={fmt} />

        {/* Consultant handoff + lead capture */}
        <ConsultantHandoff result={result} inputs={inputs} />

        {/* Disclaimer */}
        <div className="mt-6 p-4 bg-gray-50 border-l-4 border-gray-300">
          <p className="text-xs text-gray-500">
            <strong>Disclaimer:</strong> This plan is generated using AI-assisted analysis and market salary data. Recommendations are based on typical patterns for companies at your stated stage and should be treated as strategic guidance, not a guarantee. For tailored advice, <Link to="/contact" className="text-[#B8860B] underline">contact us directly</Link>.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

// Result Tabs Component — organizes outputs into sections
const ResultTabs = ({ result, sym, fmt }) => {
  const [activeSection, setActiveSection] = useState("hires");

  const sections = [
    { id: "hires", label: "Hiring Plan" },
    { id: "jd", label: "Job Description" },
    { id: "interview", label: "Interview Guide" },
    { id: "sources", label: "Candidate Sources" },
    { id: "strategy", label: "Strategy" },
    { id: "roi", label: "ROI Analysis" },
  ];

  return (
    <div>
      {/* Section tabs */}
      <div className="flex flex-wrap gap-1 mb-6 border-b border-gray-200">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-all border-b-2 ${
              activeSection === s.id ? "border-[#B8860B] text-[#1A1A1A]" : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
            data-testid={`result-tab-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Hiring Plan */}
      {activeSection === "hires" && (
        <div>
          <div className="mb-8">
            <h3 className="font-display text-xl mb-4">Recommended Hiring Plan</h3>
            <div className="space-y-4">
              {result.recommended_hires?.map((hire, i) => (
                <HireCard key={i} hire={hire} sym={sym} fmt={fmt} />
              ))}
            </div>
          </div>

          {result.do_not_hire?.length > 0 && (
            <div className="mb-8">
              <h3 className="font-display text-xl mb-4 flex items-center gap-2">
                <Ban size={20} className="text-red-400" /> Roles to Avoid (For Now)
              </h3>
              <div className="space-y-3">
                {result.do_not_hire.map((item, i) => (
                  <div key={i} className="p-4 bg-red-50 border-l-4 border-red-300">
                    <p className="font-medium text-sm text-red-800 mb-1">Don't hire: {item.title}</p>
                    <p className="text-sm text-gray-600 mb-2">{item.reason}</p>
                    <p className="text-xs text-gray-400">Hire when: {item.hire_when}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Job Description */}
      {activeSection === "jd" && result.job_description && (
        <div className="space-y-5" data-testid="result-jd">
          <div className="border border-gray-200 p-6">
            <h3 className="font-display text-xl mb-1">{result.job_description.title}</h3>
            {result.job_description.work_arrangement && (
              <p className="text-xs text-[#B8860B] mb-4">{result.job_description.work_arrangement}</p>
            )}

            {result.job_description.about_company && (
              <div className="mb-4">
                <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-2">About the Company</p>
                <p className="text-sm text-gray-700">{result.job_description.about_company}</p>
              </div>
            )}

            {result.job_description.role_overview && (
              <div className="mb-4">
                <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-2">The Role</p>
                <p className="text-sm text-gray-700">{result.job_description.role_overview}</p>
              </div>
            )}

            {result.job_description.responsibilities?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-2">Responsibilities</p>
                <ul className="space-y-1.5">
                  {result.job_description.responsibilities.map((r, i) => (
                    <li key={i} className="text-sm text-gray-700 pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-[#B8860B] before:rounded-full">{r}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.job_description.requirements?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-2">Requirements</p>
                <ul className="space-y-1.5">
                  {result.job_description.requirements.map((r, i) => (
                    <li key={i} className="text-sm text-gray-700 pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-[#1A1A1A] before:rounded-full">{r}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.job_description.nice_to_have?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Nice to Have</p>
                <ul className="space-y-1.5">
                  {result.job_description.nice_to_have.map((n, i) => (
                    <li key={i} className="text-sm text-gray-500 pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:bg-gray-300 before:rounded-full">{n}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.job_description.compensation_summary && (
              <div className="mt-4 p-4 bg-[#FAF9F6]">
                <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-1">Compensation</p>
                <p className="text-sm text-gray-700">{result.job_description.compensation_summary}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Interview Framework */}
      {activeSection === "interview" && result.interview_framework && (
        <div className="space-y-4" data-testid="result-interview">
          {["stage_1_screen", "stage_2_technical", "stage_3_culture", "stage_4_founder"].map((stageKey) => {
            const stage = result.interview_framework[stageKey];
            if (!stage) return null;
            return (
              <div key={stageKey} className="border border-gray-200">
                <div className="bg-[#FAF9F6] px-5 py-3 border-b border-gray-100">
                  <h4 className="font-display text-base">{stage.name}</h4>
                  {stage.focus && <p className="text-xs text-gray-500 mt-0.5">{stage.focus}</p>}
                </div>
                <div className="p-5 space-y-3">
                  {stage.questions?.map((q, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-[#B8860B] text-white text-xs flex items-center justify-center rounded-full font-medium">{i + 1}</span>
                      <p className="text-sm text-gray-700">{q}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Candidate Sources */}
      {activeSection === "sources" && result.candidate_sources && (
        <div className="space-y-5" data-testid="result-sources">
          {result.candidate_sources.primary?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-3">Primary Channels</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {result.candidate_sources.primary.map((src, i) => (
                  <div key={i} className="p-3 bg-[#FAF9F6] border border-gray-100 text-sm text-gray-700 flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#B8860B] rounded-full flex-shrink-0"></span>
                    {src}
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.candidate_sources.secondary?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Secondary Channels</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {result.candidate_sources.secondary.map((src, i) => (
                  <div key={i} className="p-3 border border-gray-100 text-sm text-gray-500 flex items-center gap-2">
                    <span className="w-2 h-2 bg-gray-300 rounded-full flex-shrink-0"></span>
                    {src}
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.candidate_sources.sourcing_tips && (
            <div className="p-4 bg-[#1A1A1A] text-white">
              <p className="text-xs text-[#B8860B] uppercase tracking-wider mb-2">Pro Tip</p>
              <p className="text-sm text-gray-300">{result.candidate_sources.sourcing_tips}</p>
            </div>
          )}

          {/* Per-hire sources */}
          {result.recommended_hires?.some(h => h.candidate_sources?.length > 0) && (
            <div>
              <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-3">Sources by Role</p>
              {result.recommended_hires.filter(h => h.candidate_sources?.length > 0).map((hire, i) => (
                <div key={i} className="mb-3 p-3 border border-gray-100">
                  <p className="text-sm font-medium text-gray-800 mb-2">{hire.title}</p>
                  <div className="flex flex-wrap gap-2">
                    {hire.candidate_sources.map((src, j) => (
                      <span key={j} className="text-xs px-2 py-1 bg-[#FAF9F6] border border-gray-100 text-gray-600">{src}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hiring Strategy */}
      {activeSection === "strategy" && result.hiring_strategy && (
        <div className="space-y-5" data-testid="result-strategy">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-[#FAF9F6]">
              <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-2">Approach</p>
              <p className="text-sm text-gray-700">{result.hiring_strategy.approach}</p>
            </div>
            <div className="p-4 bg-[#FAF9F6]">
              <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-2">Timeline</p>
              <p className="text-sm text-gray-700">{result.hiring_strategy.timeline}</p>
            </div>
          </div>

          {result.hiring_strategy.key_selling_points?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#B8860B] uppercase tracking-wider mb-3">What Will Attract Candidates</p>
              <div className="space-y-2">
                {result.hiring_strategy.key_selling_points.map((pt, i) => (
                  <div key={i} className="p-3 bg-green-50 border-l-4 border-green-300 text-sm text-gray-700">{pt}</div>
                ))}
              </div>
            </div>
          )}

          {result.hiring_strategy.red_flags_to_watch?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-red-500 uppercase tracking-wider mb-3">Red Flags to Watch</p>
              <div className="space-y-2">
                {result.hiring_strategy.red_flags_to_watch.map((rf, i) => (
                  <div key={i} className="p-3 bg-red-50 border-l-4 border-red-300 text-sm text-gray-700">{rf}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ROI Analysis */}
      {activeSection === "roi" && result.roi_analysis && (
        <div data-testid="result-roi">
          <div className="bg-[#FAF9F6] p-6 space-y-4">
            <div>
              <p className="text-xs text-[#B8860B] uppercase tracking-wider mb-1">Total Investment</p>
              <p className="font-display text-xl">{fmt(result.roi_analysis.total_investment, sym)} / year</p>
            </div>
            <div>
              <p className="text-xs text-[#B8860B] uppercase tracking-wider mb-1">Expected Return</p>
              <p className="text-sm text-gray-700">{result.roi_analysis.expected_return_narrative}</p>
            </div>
            <div>
              <p className="text-xs text-[#B8860B] uppercase tracking-wider mb-1">Savings vs Over-hiring</p>
              <p className="text-sm text-gray-700">{result.roi_analysis.savings_vs_overhiring}</p>
            </div>
            <div>
              <p className="text-xs text-[#B8860B] uppercase tracking-wider mb-1">Break-even Estimate</p>
              <p className="text-sm text-gray-700">{result.roi_analysis.break_even_estimate}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Hire Card Component
const HireCard = ({ hire, sym, fmt }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-gray-200 hover:border-[#B8860B]/30 transition-colors">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 text-left flex items-start justify-between gap-4"
        data-testid={`hire-card-${hire.priority}`}
      >
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 bg-[#B8860B] text-white font-display text-sm flex items-center justify-center">
            {hire.priority}
          </span>
          <div>
            <h4 className="font-display text-lg">{hire.title}</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {hire.function} | {hire.seniority} | {hire.experience_years} | Equity: {hire.equity_range}
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-display text-lg text-[#B8860B]">
            {fmt(hire.salary_range_low, sym)} - {fmt(hire.salary_range_high, sym)}
          </p>
          <p className="text-xs text-gray-400">per year</p>
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 pt-0 border-t border-gray-100 space-y-3">
          <div>
            <p className="text-xs text-[#B8860B] font-medium uppercase mb-1">Why This Hire Now</p>
            <p className="text-sm text-gray-700">{hire.why_now}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">Key Skills</p>
              <div className="flex flex-wrap gap-1">
                {hire.key_skills?.map((s, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-[#FAF9F6] border border-gray-100 text-gray-700">{s}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">Must-have Experience</p>
              <p className="text-sm text-gray-700">{hire.must_have_experience}</p>
            </div>
          </div>
          {hire.nice_to_have && (
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">Nice to Have</p>
              <p className="text-sm text-gray-600">{hire.nice_to_have}</p>
            </div>
          )}
          <div className="bg-[#FAF9F6] p-3">
            <p className="text-xs text-[#B8860B] font-medium uppercase mb-1">What to Expect in 90 Days</p>
            <p className="text-sm text-gray-700">{hire.first_90_days}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-green-50 p-3">
              <p className="text-xs text-green-700 font-medium mb-1">Expected ROI</p>
              <p className="text-sm text-gray-700">{hire.roi_estimate}</p>
            </div>
            <div className="bg-red-50 p-3">
              <p className="text-xs text-red-600 font-medium mb-1">Hiring Risk</p>
              <p className="text-sm text-gray-700">{hire.hiring_risk}</p>
            </div>
          </div>
          {hire.stage_fit_note && (
            <div className="p-3 border-l-4 border-[#B8860B] bg-[#B8860B]/5">
              <p className="text-xs text-[#B8860B] font-medium mb-1">Stage-Fit Note</p>
              <p className="text-sm text-gray-700">{hire.stage_fit_note}</p>
            </div>
          )}
          {hire.candidate_sources?.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 font-medium mb-1">Where to Find This Hire</p>
              <div className="flex flex-wrap gap-1">
                {hire.candidate_sources.map((src, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 bg-[#FAF9F6] border border-gray-100 text-gray-600">{src}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Consultant handoff + lead capture — converts a generated plan into a warm lead
const ConsultantHandoff = ({ result, inputs }) => {
  const location = useLocation();
  const ref = new URLSearchParams(location.search).get("ref") || undefined;
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "" });

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { setErr("Please add your name and email."); return; }
    setSending(true); setErr(null);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        business_description: inputs.business_description,
        funding_stage: inputs.funding_stage,
        location: inputs.location,
        plan_summary: result.summary,
        recommended_titles: (result.recommended_hires || []).map((h) => h.title),
        total_annual_cost: result.total_annual_cost,
        currency: result.currency,
        ref,
      };
      const res = await fetch(`${API_BASE}/api/tools/planner-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("failed");
      setDone(true);
    } catch {
      setErr("Something went wrong. Please try again or email hello@magnuskor.com.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="p-8 bg-[#1A1A1A] text-white text-center mt-8" data-testid="handoff-success">
        <Check size={28} className="text-[#B8860B] mx-auto mb-3" />
        <h3 className="font-display text-xl mb-2">Your plan is on its way to our team</h3>
        <p className="text-sm text-gray-400">A Magnus Kor consultant will review it and reach out shortly with next steps on executing these hires.</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 bg-[#1A1A1A] text-white mt-8" data-testid="handoff-card">
      <div className="text-center mb-6">
        <h3 className="font-display text-xl mb-2">Ready to execute this plan?</h3>
        <p className="text-sm text-gray-400 max-w-lg mx-auto">Have a Magnus Kor consultant review your plan and run these searches for you. We'll bring your generated plan to the conversation.</p>
      </div>

      {!open ? (
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => setOpen(true)} className="btn-luxury btn-luxury-gold inline-flex" data-testid="handoff-open">
            Have a consultant review this plan <ArrowRight size={14} />
          </button>
          <Link to="/for-startups#contact-form" className="btn-luxury btn-luxury-outline-light inline-flex items-center justify-center border border-white/30 text-white hover:bg-white/10 px-6 py-3 text-sm transition-colors">
            Or start a general conversation
          </Link>
        </div>
      ) : (
        <div className="max-w-xl mx-auto space-y-3" data-testid="handoff-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text" placeholder="Your name *" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="px-4 py-3 bg-white/5 border border-white/20 text-white placeholder-gray-500 focus:border-[#B8860B] focus:outline-none"
              data-testid="handoff-name"
            />
            <input
              type="email" placeholder="Work email *" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="px-4 py-3 bg-white/5 border border-white/20 text-white placeholder-gray-500 focus:border-[#B8860B] focus:outline-none"
              data-testid="handoff-email"
            />
          </div>
          <input
            type="text" placeholder="Company (optional)" value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            className="w-full px-4 py-3 bg-white/5 border border-white/20 text-white placeholder-gray-500 focus:border-[#B8860B] focus:outline-none"
            data-testid="handoff-company"
          />
          {err && <p className="text-sm text-red-400" data-testid="handoff-error">{err}</p>}
          <button onClick={submit} disabled={sending} className="btn-luxury btn-luxury-gold w-full disabled:opacity-50" data-testid="handoff-submit">
            {sending ? <span className="flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Sending...</span> : <span className="flex items-center justify-center gap-2">Send my plan to a consultant <ArrowRight size={14} /></span>}
          </button>
          <p className="text-[11px] text-gray-500 text-center">We'll only use your details to follow up on this plan. No spam.</p>
        </div>
      )}
    </div>
  );
};

export default ToolsPage;
